import { useEffect, useState } from 'react';
import { Button, Drawer, List } from '@material-ui/core';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  IconShield,
  IconMapPins,
  IconChartBar,
  IconMessageCircle,
  IconWallet,
  IconTicket,
  IconTruckDelivery,
} from '@tabler/icons';

import { NavItem } from '#root/components';
import * as ROUTES from '#root/constants/navigation';
import * as VARIABLES from '#root/constants/variables';
import { useResponsive, useAuth } from '#root/hooks';
import {
  AwardIcon,
  BriefcaseOutlineIcon,
  InboxIcon,
  PackageIcon,
  PlanenIcon,
  MoneyBagIcon,
  PersonOutlineIcon,
  PinIcon,
  ShoppingBagIcon,
  ScooterIcon,
} from '#root/icons';
import styles from './sidebar.module.scss';

interface SidebarProps {
  onMobileClose?: () => void;
  openMobile?: boolean;
}

interface NavItemModel {
  alert?: boolean;
  done?: boolean;
  external?: boolean;
  href?: string;
  icon?: any;
  title: string;
  type?: 'item' | 'separator'; // Nuevo: permite separadores
}

const Sidebar = (props: SidebarProps) => {
  const { onMobileClose = () => { }, openMobile = false } = props;
  const {
    usuario,
    traveler_trips_count,
    global_is_admin,
    global_is_viajero,
    userRole,
  } = useAuth();
  const { isDesktop } = useResponsive();
  const location = useLocation();

  // Determinar sección INICIAL solo al cargar el componente (una sola vez)
  const getInitialSection = (): 'viajero' | 'comprador' => {
    if (location.pathname.startsWith('/viajero')) return 'viajero';
    if (location.pathname.startsWith('/comprador') || location.pathname.startsWith('/usuario'))
      return 'comprador';
    // Default basado en userRole
    return userRole === 'viajero' ? 'viajero' : 'comprador';
  };

  const [activeSection, setActiveSection] = useState<'viajero' | 'comprador'>(
    getInitialSection()
  );

  // NO sincronizar con cambios de ruta - el toggle solo cambia cuando el usuario lo clickea manualmente

  // Cerrar sidebar mobile al cambiar de ruta
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const hasTrip = traveler_trips_count > 0 ? true : false;

  // Items de navegación de Viajero
  const travelerItems: NavItemModel[] = [
    {
      icon: PersonOutlineIcon,
      href: ROUTES.USER_PROFILE,
      title: 'Mi Perfil',
    },
    {
      icon: IconMessageCircle,
      href: ROUTES.USER_CHAT,
      title: 'Mis Chats',
    },
    {
      type: 'separator',
      title: '',
    },
    {
      href: ROUTES.TRAVELER_DASHBOARD,
      icon: IconChartBar,
      title: 'Mi Resumen',
    },
    {
      href: ROUTES.TRAVELER_TRIPS,
      icon: PlanenIcon,
      title: 'Mis Viajes',
      alert: !hasTrip,
    },
    {
      href: ROUTES.TRAVELER_ORDERS,
      icon: PackageIcon,
      title: 'Ver Pedidos',
    },
    ...(usuario?.traveler_view_orders_pre
      ? [
        {
          href: ROUTES.TRAVELER_ORDERS_PRE,
          icon: PackageIcon,
          title: 'Ver Pedidos Pre',
        },
      ]
      : []),
    {
      href: ROUTES.TRAVELER_OFFERS,
      icon: InboxIcon,
      title: 'Mis Ofertas',
    },
    {
      href: ROUTES.TRAVELER_DEALS,
      icon: BriefcaseOutlineIcon,
      title: 'Mis Acuerdos',
    },
    {
      href: ROUTES.TRAVELER_EARNINGS,
      icon: MoneyBagIcon,
      title: 'Mis Ganancias',
    },
    {
      type: 'separator',
      title: '',
    },
    {
      href: ROUTES.TRAVELER_DELIVERY_SETUP,
      icon: IconMapPins,
      title: 'Configurar Entregas',
      alert: usuario?.capable_traveler_at ? false : true,
    },
    {
      href: ROUTES.TRAVELER_TRAINING,
      icon: AwardIcon,
      title: 'Capacitación',
      alert: usuario?.capable_traveler_at ? false : true,
    },
  ];

  // Items de navegación de Comprador
  const compradorItems: NavItemModel[] = [
    {
      icon: PersonOutlineIcon,
      href: ROUTES.USER_PROFILE,
      title: 'Mi Perfil',
    },
    {
      icon: IconMessageCircle,
      href: ROUTES.USER_CHAT,
      title: 'Mis Chats',
    },
    {
      type: 'separator',
      title: '',
    },
    {
      icon: ShoppingBagIcon,
      href: ROUTES.USER_ORDERS,
      title: 'Mis Pedidos',
    },
    // {
    //   icon: ScooterIcon,
    //   href: ROUTES.USER_ORDERS_STORE,
    //   title: 'Mis Pedidos Tienda',
    // },
    {
      icon: IconWallet,
      href: ROUTES.USER_WALLET,
      title: 'Mi Billetera',
    },
    {
      icon: PinIcon,
      href: ROUTES.USER_ADDRESS,
      title: 'Mis Direcciones',
    },
    {
      icon: IconTruckDelivery,
      href: ROUTES.USER_SHIPMENTS,
      title: 'Mis Envíos',
    },
    {
      icon: IconTicket,
      href: ROUTES.USER_COUPONS,
      title: 'Mis Cupones',
    },
  ];

  // Ya no necesitamos handleToggleChange - los botones llaman directamente a setActiveSection

  // Determinar qué items mostrar basado en la sección activa
  const currentItems = activeSection === 'viajero' ? travelerItems : compradorItems;

  const content = (
    <div className={styles.content}>
      {/* Toggle Buttons (Comprador / Viajero) */}
      {global_is_viajero && (
        <div className={styles.toggleContainer}>
          <button
            type="button"
            className={`${styles.toggleButton} ${activeSection === 'comprador' ? styles.active : ''}`}
            onClick={() => setActiveSection('comprador')}>
            <ShoppingBagIcon />
            <span>Comprador</span>
          </button>

          <button
            type="button"
            className={`${styles.toggleButton} ${activeSection === 'viajero' ? styles.active : ''}`}
            onClick={() => setActiveSection('viajero')}>
            <PlanenIcon />
            <span>Viajero</span>
          </button>
        </div>
      )}

      {/* Items de navegación */}
      <div className={styles.list}>
        <List style={{ padding: 0 }}>
          {currentItems.map((item, index) => {
            if (item.type === 'separator') {
              return <div key={`separator-${index}`} className={styles.separator} />;
            }
            return (
              <NavItem
                external={item.external}
                href={item.href}
                icon={item.icon}
                key={item.title}
                title={item.title}
                alert={item.alert}
                done={item.done}
              />
            );
          })}
        </List>
      </div>

      <div className={styles.space} />

      {/* Bottom section */}
      <div className={styles.bottom}>
        {/* Botón de Admin */}
        {global_is_admin && (
          <div className={styles.action}>
            <Button
              color="secondary"
              variant="outlined"
              component={'a'}
              disableElevation
              fullWidth
              href={VARIABLES.URL_PHP_PAGE + 'admin'}
              startIcon={<IconShield size={18} strokeWidth={1.5} />}>
              Admin
            </Button>
          </div>
        )}

        {/* Botón de Configuración Viajero (solo si es comprador y no tiene cuenta de viajero) */}
        {activeSection === 'comprador' && !global_is_viajero && (
          <div className={styles.action}>
            <Button
              color="primary"
              variant="outlined"
              component={RouterLink}
              disableElevation
              fullWidth
              to={ROUTES.USER_TRAVELER_SETTINGS}>
              Configuración Viajero
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isDesktop ? (
        <Drawer
          anchor="left"
          open
          PaperProps={{
            elevation: 0,
            style: {
              background: 'var(--color-background-alt)',
              borderRight: 'none',
              width: 208,
              top: 64,
              height: '100%',
              zIndex: 1000,
            },
          }}
          variant="persistent">
          {content}
        </Drawer>
      ) : (
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          PaperProps={{
            style: {
              width: 256,
              top: 57,
              background: 'var(--color-background-alt)',
            },
          }}
          style={{
            zIndex: 1200,
          }}
          variant="temporary">
          {content}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
